import {gql, useMutation, useQuery} from "@apollo/client";
import {EventType, EventTypeAccess} from "../../../../schema";
import UnexpectedErrorScene from "../../../system/UnexpectedErrorScene";
import React, {useMemo} from "react";
import cn from "classnames";
import Toggle from "../../../../ui/Toggle";
import {ButtonColour} from "../../../../ui/ButtonColour";
import Tip, {TipType} from "../../../../ui/Tip";
import {handleError, handleSuccess} from "../../../../utils/Apollo";

type LicensesTab = {
  licenseeId: string;
}

function EventTypeRow({
                        licenseeId,
                        eventType,
                        eventTypeAccesses
                      }: { licenseeId: string, eventType: EventType, eventTypeAccesses: EventTypeAccess[] }) {
  const active = !!eventTypeAccesses.find(access => access.eventTypeId === eventType.id);

  const [toggle] = useMutation(toggleMutation, {
    refetchQueries: ['EventTypeAccessQuery', 'eventTypeAccesses']
  });

  const onToggle = React.useCallback(() => {
    toggle({
      variables: {licenseeId, eventTypeId: eventType.id}
    }).then(
      () => handleSuccess(`Event type access is ${active ? 'revoked' : 'given'}`),
      (apolloError) => handleError(apolloError)
    );
  }, [toggle, eventType, active]);


  return (
    <div key={eventType.id} className={cn('flex justify-between pl-2 py-3 border-gray border-b', {
      'text-gray': !active,
    })}>
      <span>{eventType.name}</span>
      <Toggle colour={ButtonColour.BLUE} active={true} on={active} handleStateSwitch={onToggle}/>
    </div>
  )
}

export function EventTypeAccessTab({licenseeId}: LicensesTab) {

  const {loading, data, error} = useQuery<EventTypeAccessQuery>(query, {
    variables: {id: licenseeId}
  });

  const types = useMemo(() => {
    const unsorted: EventType[] = [];
    data?.eventTypes.forEach(type => unsorted.push(type));
    return unsorted.sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  if (error) {
    return <UnexpectedErrorScene
      error={error}
      errorInfo={null}
      eventId={null}
    />;
  }

  return (
    <div className="mt-8 flex flex-row">
      <div className="flex flex-col space-y-1 w-1/2">
        {!loading && !!data && (
          <>
            {types.map((eventType) =>
              <EventTypeRow licenseeId={licenseeId} eventType={eventType}
                            eventTypeAccesses={data?.eventTypeAccesses || []} key={eventType.id}/>
            )}
          </>
        )}
      </div>
      <div className="w-1/2 px-20">
        <Tip type={TipType.Advice}>
          Access to event types depends on the license held by a licensee.
          You can give or revoke access to specific event types by toggling the switches.
        </Tip>
      </div>
    </div>
  );
}

interface EventTypeAccessQuery {
  eventTypeAccesses: EventTypeAccess[];
  eventTypes: EventType[];
}

const query = gql`
  query EventTypeAccessQuery($id: ID!) {
    eventTypeAccesses: eventTypeAccesses(licenseeId: $id) {
      eventTypeId
    },
    eventTypes: eventTypes {
      id
      name
    }
  }
`;

const toggleMutation = gql`
  mutation ToggleEventTypeAccess($licenseeId: ID!, $eventTypeId: ID!) {
    toggleEventTypeAccess(licenseeId: $licenseeId, eventTypeId: $eventTypeId) {
      eventTypeId
    }
  }
`;
